/**
 * useCompetitionData - Data Fetching and Computed Values
 *
 * Single responsibility: Fetching and transforming competition data.
 * Handles all data-related logic for CompetitionView.
 */

import { useMemo } from 'react';
import { useCompetitions } from '@/hooks/useCompetitions';
import { usePickemById } from '@/api/query/usePickem';
import { usePickemKickoffRefresh } from '@/hooks/usePickemKickoffRefresh';
import { usePickemSelections } from '@/hooks/usePickemSelections';
import { useSubmitSelections } from '@/api/mutations/useSubmitSelections';
import { formatTimeRemaining, formatRelativeTime } from '@/lib/dateFormat';
import { toQuestionId } from '@/utils/type-guards';
import { isPickemEntryClosed } from '@/utils/pickem-entry';
import { mapPickemStatusToGameState } from '@/utils/type-transformers';
import { formatPrizeLabel, type UserPrizeData } from '@/utils/prize';
import { getLocalizedField } from '@/utils/localized-field';
import type { Competition, GameState } from '@/types/domain';
import i18n from '@/i18n';

interface UseCompetitionDataProps {
  gameId: string;
  onSuccessSubmit: () => void;
}

function resolveCompetitionState(
  competition: Competition | undefined,
  pickemStatus: unknown | undefined,
  gameDate: Date
): GameState {
  // Prefer detail API status — list cache can lag behind INPLAY/COMPLETE.
  if (pickemStatus != null) {
    return mapPickemStatusToGameState(pickemStatus, gameDate);
  }

  if (!competition) {
    return mapPickemStatusToGameState(undefined, gameDate);
  }

  // Time-aware remap when list still says countdown but start has passed.
  if (competition.state === 'countdown' && gameDate.getTime() <= Date.now()) {
    return 'live';
  }

  return competition.state;
}

/**
 * Hook for managing competition data and computed values
 *
 * @param gameId - The competition ID
 * @param onSuccessSubmit - Callback when submission succeeds
 * @returns All data, computed values, and selection handlers
 */
export function useCompetitionData({ gameId, onSuccessSubmit }: UseCompetitionDataProps) {
  // Data fetching
  const { getCompetition } = useCompetitions();
  const competition = getCompetition(gameId);
  const { data: pickemDetails, isLoading, error } = usePickemById(gameId);

  const kickoffTick = usePickemKickoffRefresh([
    competition?.gameDate,
    pickemDetails?.pickem.start_utc,
    pickemDetails?.pickem.start,
    pickemDetails?.pickem.start_utc_timestamp,
  ]);

  // Selection management
  const {
    selections,
    handleTeamSelect,
    handlePropSelect,
    handleOverUnderSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    favPick,
    allQuestionsAnswered,
    buildSubmissionPayload,
  } = usePickemSelections(pickemDetails);

  // Submission
  const hasExistingSelections = (pickemDetails?.pickem.selections?.length ?? 0) > 0;
  const submitMutation = useSubmitSelections({
    pickemId: gameId,
    isUpdate: hasExistingSelections,
    showToast: false,
    onSuccess: onSuccessSubmit,
  });

  // Computed values
  const selectionResults = useMemo(() => {
    if (!pickemDetails?.pickem.selections) return {};

    const results: Record<string, boolean> = {};
    pickemDetails.pickem.selections.forEach(selection => {
      if (typeof selection.selection_is_correct === 'boolean') {
        results[toQuestionId(selection.question_id)] = selection.selection_is_correct;
      }
    });

    return results;
  }, [pickemDetails]);

  const selectionPoints = useMemo(() => {
    if (!pickemDetails?.pickem.selections) return {};

    const points: Record<string, number> = {};
    pickemDetails.pickem.selections.forEach(selection => {
      points[toQuestionId(selection.question_id)] = selection.points || 0;
    });

    return points;
  }, [pickemDetails]);

  const competitionData = useMemo(() => {
    // kickoffTick re-runs resolve so OPEN + past start maps to live.
    void kickoffTick;

    const pickem = pickemDetails?.pickem as
      | (NonNullable<typeof pickemDetails>['pickem'] & {
          user_ranking?: number | null;
          total_points?: number | null;
          user_prize?: UserPrizeData | null;
        })
      | undefined;
    const myPrizeFromDetail = formatPrizeLabel(pickem?.user_prize) ?? undefined;

    const gameDate = pickem?.start_utc
      ? new Date(pickem.start_utc)
      : competition?.gameDate instanceof Date
        ? competition.gameDate
        : new Date(competition?.gameDate ?? Date.now());

    const state = resolveCompetitionState(competition, pickem?.status, gameDate);
    const hasScore =
      typeof pickem?.user_ranking === 'number' && typeof pickem?.total_points === 'number';

    // Prefer lobby/context competition fields; overlay fresher detail status/scores.
    if (competition) {
      return {
        ...competition,
        state,
        gameDate,
        myPrize: myPrizeFromDetail ?? competition.myPrize,
        finalScore: hasScore
          ? {
              rank: pickem!.user_ranking as number,
              points: pickem!.total_points as number,
            }
          : competition.finalScore,
        currentPoints:
          typeof pickem?.total_points === 'number' && state === 'live'
            ? pickem.total_points
            : competition.currentPoints,
        totalEntries:
          typeof pickem?.entries === 'number' ? pickem.entries : competition.totalEntries,
      };
    }

    // Fallback if competition not in context
    return {
      id: gameId,
      state,
      gameDate,
      title: pickem
        ? getLocalizedField(pickem, 'title', i18n.language) || i18n.t('game.fallbackTitle')
        : i18n.t('game.fallbackTitle'),
      prize: pickem ? getLocalizedField(pickem, 'prize_text', i18n.language) || '' : '',
      finalScore: hasScore
        ? {
            rank: pickem!.user_ranking as number,
            points: pickem!.total_points as number,
          }
        : undefined,
      currentPoints:
        typeof pickem?.total_points === 'number' && state === 'live'
          ? pickem.total_points
          : undefined,
      myPrize: myPrizeFromDetail,
      totalEntries: typeof pickem?.entries === 'number' ? pickem.entries : undefined,
    };
  }, [competition, pickemDetails, gameId, kickoffTick]);

  const timeDisplay = useMemo(() => {
    const gameDate =
      competitionData.gameDate instanceof Date
        ? competitionData.gameDate
        : new Date(competitionData.gameDate);

    if (!(gameDate instanceof Date) || Number.isNaN(gameDate.getTime())) {
      return '';
    }

    switch (competitionData.state) {
      case 'countdown':
        return formatTimeRemaining(gameDate);
      case 'live':
        return i18n.t('game.status.live');
      case 'final':
        return formatRelativeTime(gameDate);
      default:
        return formatRelativeTime(gameDate);
    }
  }, [competitionData]);

  // Derived state — lock entry/edits once contest start (or earliest fixture) passes,
  // even if status transition (OPEN/ACTIVE → INPLAY) has not run yet.
  // live/final: view-only. Per-question locked chrome when selection_is_correct unset;
  // graded questions use correct/incorrect surfaces instead.
  const isContestClosed = isPickemEntryClosed(pickemDetails?.pickem);
  const isResultsMode = competitionData.state === 'live' || competitionData.state === 'final';
  const isViewOnly = isContestClosed || isResultsMode;
  const submitButtonText = hasExistingSelections
    ? i18n.t('actions.updateSelections')
    : i18n.t('actions.saveChanges');

  return {
    // Data
    pickemDetails,
    competitionData,
    isLoading,
    error,

    // Selections
    selections,
    handleTeamSelect,
    handlePropSelect,
    handleOverUnderSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    favPick,
    allQuestionsAnswered,
    buildSubmissionPayload,

    // Submission
    submitMutation,
    hasExistingSelections,
    submitButtonText,

    // Computed values
    selectionResults,
    selectionPoints,
    timeDisplay,

    // Derived state
    isContestClosed,
    isViewOnly,
    isResultsMode,
  };
}
